import React, { useEffect, useState } from 'react';
import { computeBoxPlotDomain } from '../../util/RangeCalculation';
import { BAND_RED, BAND_YELLOW, BAND_GREEN } from '../../util/Colors';
import { parseResultValue } from '../../util/GenericUtil';

function BoxPlot(props) {
    const [hover, setHover] = useState(false);
    const [dividerHover, setDividerHover] = useState(null); // 'fxlow' | 'fxhigh' | null
    const [barHover, setBarHover] = useState(false);

    useEffect(() => {
        if (props.setRenderSuccess) {
            props.setRenderSuccess(true)
        }
    }, [])
    useEffect(() => {
        // no-op for now; intentionally separate from the diamond's hover logic
        return () => {};
    }, [dividerHover]);
    useEffect(() => {
        // hook for analytics or side-effects if you want later
        return () => {};
    }, [barHover, dividerHover]);

    // Early validation
    if (props == null || props.result == null) return <div>Loading...</div>;

    const result = parseResultValue(props.result);
    if (!Number.isFinite(result)) return <div className="boxPlotMessage">Text result</div>;

    const domain = computeBoxPlotDomain(result, props.llow, props.lhigh, props.fxlow, props.fxhigh);
    if (!domain) return <div className="boxPlotMessage">No reference range</div>;
    const { llow, lhigh, fxL, fxH, displayMin, displayMax } = domain;

    const CustomBoxPlot = ({ width, height }) => {
    const plotHeight = height - 80;
    const plotWidth  = width - 100;
    const startX = 60;
    const startY = 40;

    const span = Math.max(1e-9, displayMax - displayMin);
    const getX = (v) => startX + ((v - displayMin) / span) * plotWidth;

    const llowX   = getX(llow);
    const lhighX  = getX(lhigh);
    const fxlowX  = getX(fxL);
    const fxhighX = getX(fxH);
    const resultX = getX(result);
    const resultY = startY + plotHeight / 2;

    const outerRoundedPath = (x, y, w, h, r, side /* 'left' | 'right' */) => {
        if (w <= 0 || h <= 0) return '';
        const rr = Math.max(0, Math.min(r, h / 2, w / 2)); // clamp radius

        if (rr === 0) {
            return `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`; // square
        }

        if (side === 'left') {
            // Round top-left & bottom-left only
            return [
            `M ${x + rr} ${y}`,
            `H ${x + w}`,
            `V ${y + h}`,
            `H ${x + rr}`,
            `A ${rr} ${rr} 0 0 1 ${x} ${y + h - rr}`,
            `V ${y + rr}`,
            `A ${rr} ${rr} 0 0 1 ${x + rr} ${y}`,
            `Z`,
            ].join(' ');
        }

        // side === 'right' → round top-right & bottom-right only
        return [
            `M ${x} ${y}`,
            `H ${x + w - rr}`,
            `A ${rr} ${rr} 0 0 1 ${x + w} ${y + rr}`,
            `V ${y + h - rr}`,
            `A ${rr} ${rr} 0 0 1 ${x + w - rr} ${y + h}`,
            `H ${x}`,
            `Z`,
        ].join(' ');
    };

    return (
    <svg width={width} height={height} role="img" aria-label={`Result ${result}`}>
        {/* Bands */}
        <path d={outerRoundedPath(startX, startY, Math.max(0, llowX - startX), plotHeight, 8, 'left')} fill={BAND_RED}/>
        <rect x={llowX}   y={startY} width={Math.max(0, fxlowX - llowX)}   height={plotHeight} fill={BAND_YELLOW} />
        <rect x={fxhighX} y={startY} width={Math.max(0, lhighX - fxhighX)} height={plotHeight} fill={BAND_YELLOW} />
        <path d={outerRoundedPath(lhighX, startY, Math.max(0, startX + plotWidth - lhighX), plotHeight, 8, 'right')} fill={BAND_RED}/>

        {/* Everything inside this <g> counts as the "bar" hover area */}
        <g
        onMouseEnter={() => setBarHover(true)}
        onMouseLeave={() => setBarHover(false)}
        >
        <rect x={fxlowX}  y={startY} width={Math.max(0, fxhighX - fxlowX)} height={plotHeight} fill={BAND_GREEN} />
        </g>

        {/* Dividers (keep their own hover if you still want single-label-on-line) */}
        <line x1={fxlowX}  y1={startY} x2={fxlowX}  y2={startY + plotHeight} stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
        <line x1={fxhighX} y1={startY} x2={fxhighX} y2={startY + plotHeight} stroke="rgba(0,0,0,0.2)" strokeWidth="1" />

        {/* Result diamond (unchanged) */}
        <g
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            aria-label={`Result ${result}`}
            role="graphics-symbol"
            style={{ cursor: 'default' }}
        >
            <polygon
            points={`${resultX-10},${resultY} ${resultX},${resultY - 10} ${resultX+10},${resultY} ${resultX},${resultY + 10}`}
            fill="#1f2937"
            stroke="white"
            strokeWidth="2"
            />
            {hover && (
            <text
                x={resultX}
                y={resultY - 15}
                textAnchor="middle"
                fontSize="12"
                fontWeight="600"
                fill="black"
                pointerEvents="none"
            >
                {String(result)}
            </text>
            )}

        {/* Fx labels: show both when the bar is hovered */}
        {(barHover || dividerHover === 'fxlow') && (
            <text
            x={fxlowX}
            y={startY - 6}
            textAnchor="middle"
            fontSize="11"
            fill="black"
            pointerEvents="none"
            >
            {fxL}
            </text>
        )}
        {(barHover || dividerHover === 'fxhigh') && (
            <text
            x={fxhighX}
            y={startY - 6}
            textAnchor="middle"
            fontSize="11"
            fill="black"
            pointerEvents="none"
            >
            {fxH}
            </text>
        )}
        </g>

        {/* Lab Labels (use clamped fx values) */}
        {!barHover && (
        <>
            <text
            x={llowX}
            y={startY - 10}
            textAnchor="middle"
            fontSize="12"
            fill="#6b7280"
            pointerEvents="none"
            >
            {llow}
            </text>

            <text
            x={lhighX}
            y={startY - 10}
            textAnchor="middle"
            fontSize="12"
            fill="#6b7280"
            pointerEvents="none"
            >
            {lhigh}
            </text>
        </>
        )}
    </svg>
    );

    };
        return (
            <div className="w-full h-full">
                <CustomBoxPlot width={400} height={90} />
            </div>
        );
    }

export default BoxPlot;